import { ENDPOINT_FEEDBACK } from "@constants/urls";
import { currentAppVersion } from "@helpers/expo-extras";
import { Platform } from "react-native";
import type { ReviewCacheData } from "../hooks/useReviewCache";

const NUM_DAYS_THRESHOLD = 3;
const SCORE_THRESHOLD = 75;

export type FeedbackType = "Review" | "Deletion" | "Content Flagged";

const getShouldShowReviewPrompt = (
	isPremiumUser: boolean,
	reviewCache: ReviewCacheData,
) => {
	const {
		lastShownPromptDate,
		lastAppVersion,
		userScore,
		hasLeftReview,
		hasCouldBeBetter,
		hasSaidMaybeLater,
	} = reviewCache;

	if (!isPremiumUser) {
		return false;
	}

	if (userScore === null || userScore < SCORE_THRESHOLD) {
		return false;
	}

	if (hasLeftReview) {
		return false;
	}

	if (hasCouldBeBetter) {
		if (lastAppVersion === currentAppVersion) {
			return false;
		}
	}

	if (lastShownPromptDate !== null && (hasCouldBeBetter || hasSaidMaybeLater)) {
		const daysSinceLastPrompt =
			(Date.now() - lastShownPromptDate) / (24 * 60 * 60 * 1000);
		if (daysSinceLastPrompt < NUM_DAYS_THRESHOLD) {
			return false;
		}
	}

	return true;
};

type FeedbackParams = {
	email: string;
	feedback: string;
	type: FeedbackType;
	rating?: "positive" | "negative";
	appLanguage: string;
	examName?: string;
	view: string;
	projectId: number;
	organizationId: number;
	productId: string;
	currentScreen?: string;
	featureName?: string;
	itemId?: string;
	sessionId?: string;
	responseId?: string;
	aiThreadId?: string;
	aiRunId?: string;
	aiTraceId?: string;
	itemVersionId?: string;
	sessionType?: string;
};
const sendFeedback = async (params: FeedbackParams) => {
	const {
		email,
		feedback,
		type = "Review",
		appLanguage,
		examName,
		view,
		projectId,
		organizationId,
		productId,
		currentScreen,
		featureName,
		itemId,
		sessionId,
		responseId,
		aiThreadId,
		aiRunId,
		aiTraceId,
		itemVersionId,
		sessionType,
		rating,
	} = params;

	const body = JSON.stringify({
		email,
		appVersion: currentAppVersion,
		projectId: projectId.toString(),
		organizationId: organizationId.toString(),
		feedback,
		platform: Platform.OS,
		currentScreen,
		currentUrl: view,
		featureName,
		itemId,
		sessionId,
		responseId,
		aiThreadId,
		aiRunId,
		aiTraceId,
		appContext: examName,
		bundleId: productId,
		source: type,
		itemVersionId,
		sessionType,
		rating,
		appLanguage,
	});

	await fetch(ENDPOINT_FEEDBACK, {
		method: "POST",
		body,
	});
};
export { getShouldShowReviewPrompt, sendFeedback };
